import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Save, 
  Upload,
  Building2,
  Phone,
  Mail,
  Shield,
  FileText,
  MapPin,
  User,
  CreditCard,
  Banknote
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const CreateVendor = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fleetSysId: '',
    VendorName: '',
    OwnerName: '',
    UPIId: '',
    ownerPhone: '',
    ownerEmail: '',
    city: '',
    country: '',
    fullAddress: '',
    state: '',
    ownerProof: '',
    gst: '',
    companyPANNumber: '',
    location: {
      type: 'Point',
      coordinates: [0, 0]
    },
    bankDetails: {
      bankName: '',
      ifsc: '',
      branch: '',
      accountHolderName: '',
      accountNumber: ''
    }
  });

  const [files, setFiles] = useState({
    aadhar: null,
    ownerPAN: null,
    companyPAN: null,
    cin: null,
    gst: null,
    ownerPic: null,
    ownerProof: null,
    pan: null,
    tan: null
  });

  const createVendorMutation = useMutation(
    (data) => api.post('/vendors', data),
    {
      onSuccess: () => {
        toast.success('Vendor created successfully');
        navigate('/vendors');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create vendor');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fleetSysId || !formData.VendorName || !formData.OwnerName || 
        !formData.ownerPhone || !formData.ownerEmail || !formData.city || 
        !formData.country || !formData.fullAddress || !formData.state) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      
      // Add form data
      Object.keys(formData).forEach(key => {
        if (key === 'location' || key === 'bankDetails') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add files
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      await createVendorMutation.mutateAsync(formDataToSend);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasPermission('vendors', 'write')) {
    return (
      <div className="empty-state">
        <Shield className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to create vendors.
        </p>
        <Link to="/vendors" className="btn btn-primary mt-4">
          Back to Vendors
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-left">
            <Link to="/vendors" className="btn btn-secondary mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Vendors
            </Link>
            <h1 className="page-title">
              <Building2 className="page-title-icon" />
              Create New Vendor
            </h1>
            <p className="page-description">
              Add a new vendor to the system
            </p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="form-container">
          <form onSubmit={handleSubmit} className="form">
            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title">
                <Building2 className="w-5 h-5" />
                Basic Information
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Fleet System ID *</label>
                  <input
                    type="text"
                    name="fleetSysId"
                    value={formData.fleetSysId}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter fleet system ID"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Vendor Name *</label>
                  <input
                    type="text"
                    name="VendorName"
                    value={formData.VendorName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter vendor name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Owner Name *</label>
                  <input
                    type="text"
                    name="OwnerName"
                    value={formData.OwnerName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter owner name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">UPI ID</label>
                  <input
                    type="text"
                    name="UPIId"
                    value={formData.UPIId}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter UPI ID"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3 className="form-section-title">
                <Phone className="w-5 h-5" />
                Contact Information
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    name="ownerEmail"
                    value={formData.ownerEmail}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h3 className="form-section-title">
                <MapPin className="w-5 h-5" />
                Address Information
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter city"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter state"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Country *</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter country"
                    required
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="form-label">Full Address *</label>
                  <textarea
                    name="fullAddress"
                    value={formData.fullAddress}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter full address"
                    rows="3"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="form-section">
              <h3 className="form-section-title">
                <FileText className="w-5 h-5" />
                Business Information
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">GST Number</label>
                  <input
                    type="text"
                    name="gst"
                    value={formData.gst}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter GST number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Company PAN Number</label>
                  <input
                    type="text"
                    name="companyPANNumber"
                    value={formData.companyPANNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter company PAN number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Owner Proof</label>
                  <input
                    type="text"
                    name="ownerProof"
                    value={formData.ownerProof}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter owner proof"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="form-section">
              <h3 className="form-section-title">
                <Banknote className="w-5 h-5" />
                Bank Details
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    name="bankDetails.bankName"
                    value={formData.bankDetails.bankName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter bank name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">IFSC Code</label>
                  <input
                    type="text"
                    name="bankDetails.ifsc"
                    value={formData.bankDetails.ifsc}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter IFSC code"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Branch</label>
                  <input
                    type="text"
                    name="bankDetails.branch"
                    value={formData.bankDetails.branch}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter branch name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Account Holder Name</label>
                  <input
                    type="text"
                    name="bankDetails.accountHolderName"
                    value={formData.bankDetails.accountHolderName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter account holder name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Account Number</label>
                  <input
                    type="text"
                    name="bankDetails.accountNumber"
                    value={formData.bankDetails.accountNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter account number"
                  />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h3 className="form-section-title">
                <FileText className="w-5 h-5" />
                Documents
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Aadhar Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'aadhar')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Owner PAN</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'ownerPAN')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Company PAN</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'companyPAN')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">CIN Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'cin')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">GST Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'gst')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Owner Photo</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'ownerPic')}
                    className="form-input"
                    accept="image/*"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Owner Proof Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'ownerProof')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PAN Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'pan')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">TAN Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, 'tan')}
                    className="form-input"
                    accept="image/*,.pdf"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <Link to="/vendors" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Create Vendor
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateVendor; 